// src/utilidades/pdfTicket.js
// PDF del ticket, generado en el navegador: sin librerías, sin Firebase y sin
// Functions (costo cero). Formato de ticket térmico de 80 mm: logo del gym
// arriba, letra monoespaciada (Courier, incluida en todo lector de PDF) para que
// los importes queden alineados, y líneas finas entre secciones. Luego se
// comparte (menú del celular → WhatsApp con el archivo adjunto) o se descarga.
import { simboloMoneda } from '../dominio/moneda';
import { modeloTicket } from '../dominio/comprobanteImpreso';
import { recortarMargenLogo } from './logoGym';

const ANCHO = 226.77; // 80 mm en puntos
const MARGEN = 11.34; // 4 mm
const CUERPO = 8.5;
const COLUMNAS = Math.floor((ANCHO - 2 * MARGEN) / (CUERPO * 0.6)); // Courier: 0.6 em por carácter
const LOGO_MAX_ANCHO = 119; // 42 mm
const LOGO_MAX_ALTO = 57; // 20 mm

const soles = (centimos) => `${simboloMoneda()} ${((Number(centimos) || 0) / 100).toFixed(2)}`;

/** Corta un texto en renglones de `ancho` caracteres, sin partir palabras si se puede. */
export const partir = (texto, ancho) => {
  const renglones = [];
  for (const parrafo of String(texto ?? '').split('\n')) {
    let actual = '';
    for (const palabra of parrafo.split(/\s+/).filter(Boolean)) {
      let resto = palabra;
      while (resto.length > ancho) {
        if (actual) { renglones.push(actual); actual = ''; }
        renglones.push(resto.slice(0, ancho)); resto = resto.slice(ancho);
      }
      if (!actual) actual = resto;
      else if (actual.length + 1 + resto.length <= ancho) actual += ` ${resto}`;
      else { renglones.push(actual); actual = resto; }
    }
    renglones.push(actual);
  }
  return renglones;
};

// Datos sueltos de una venta (datosTicketVenta) → modelo común del comprobante.
const modeloDesdeDatos = (datos) => {
  const modelo = modeloTicket({
    tipo: 'venta', referenciaInterna: datos.numero, estado: datos.anulada ? 'anulada' : '', fecha: datos.fecha,
    clienteNombre: datos.cliente === null ? null : datos.cliente, clienteDocumento: datos.clienteDocumento,
    metodoPago: datos.metodo, voucherTarjeta: datos.voucher, sinIgv: datos.sinIgv,
    calculo: {
      totalCentimos: datos.totalCentimos,
      items: (datos.items || []).map((i) => ({
        cantidadMilesimas: Math.round(Number(i.cantidad ?? 1) * 1000), unidad: i.unidad || 'NIU', codigo: i.codigo || '',
        descripcion: `${i.nombre || 'Producto'}${i.variante ? ` (${i.variante})` : ''}`, totalCentimos: i.subtotalCentimos,
      })),
    },
  }, datos.gym || {});
  return { ...modelo, titulo: datos.titulo || modelo.titulo, pie: datos.nota ? [datos.nota] : modelo.pie };
};

/**
 * Renglones del ticket (puro): [{ texto, estilo: 'titulo'|'subtitulo'|'centro'|'texto'|'negrita'|'total'|'regla', derecha? }].
 * Mismo contenido y orden que el ticket impreso (htmlComprobante).
 * datos: { modelo } (de comprobanteImpreso) o los datos sueltos de una venta:
 *        { gym: { nombre, direccion, logo, ticket }, titulo, numero, fecha (ms|Date), cliente, metodo, voucher,
 *          items: [{ cantidad, nombre, variante, subtotalCentimos }], totalCentimos, nota, prueba, anulada }
 */
export function renglonesTicket(datos) {
  const m = datos.modelo || modeloDesdeDatos(datos);
  const e = m.emisor || {};
  const r = [];
  const add = (texto, estilo = 'texto', derecha = '') => r.push({ texto, estilo, derecha });
  const centrado = (texto, estilo = 'centro') => partir(texto, estilo === 'centro' ? COLUMNAS : 30).forEach((t) => add(t, estilo));
  const regla = () => add('', 'regla');
  partir(e.encabezado || e.nombre || 'Gimnasio', 26).forEach((t) => add(t, 'titulo'));
  if (e.subtitulo) centrado(e.subtitulo, 'subtitulo');
  if (e.ruc) add(`RUC: ${e.ruc}`, 'subtitulo');
  if (e.direccion) centrado(e.direccion);
  [e.telefono && `Telf: ${e.telefono}`, e.correo && `Correo: ${e.correo}`, e.web && `Web: ${e.web}`].filter(Boolean).forEach((t) => centrado(t));
  regla();
  add(m.titulo, 'subtitulo');
  if (m.numero) add(m.numero, 'subtitulo');
  if (datos.prueba) add('PAGO DE PRUEBA - SIN VALOR', 'centro');
  if (m.anulada) add('*** VENTA ANULADA ***', 'titulo');
  // cliente: null en la vista pública (no muestra datos personales).
  if (m.cliente) {
    centrado(m.cliente.nombre);
    if (m.cliente.documento) add(m.cliente.documento, 'subtitulo');
  }
  if (m.fecha) add(`FECHA: ${m.fecha}${m.hora ? `  HORA: ${m.hora}` : ''}`);
  regla();
  add('CANT U.M  DESCRIPCION', 'negrita', 'TOTAL');
  for (const item of m.items || []) {
    const monto = soles(item.totalCentimos);
    const cantidad = Number.isInteger(item.cantidad) ? item.cantidad : item.cantidad.toFixed(3).replace(/0+$/, '');
    const renglones = partir(`${cantidad} ${item.unidad}  ${item.descripcion}`, COLUMNAS - monto.length - 1);
    renglones.forEach((t, i) => add(t, 'texto', i === renglones.length - 1 ? monto : ''));
    if (item.cantidad !== 1) add(`   ${soles(item.precioCentimos)} c/u`);
  }
  regla();
  if (m.totales?.conIgv) {
    add('TOTAL GRAVADO', 'texto', soles(m.totales.gravadaCentimos));
    add('I.G.V. (18%)', 'texto', soles(m.totales.igvCentimos));
  }
  add('TOTAL', 'total', soles(m.totales?.totalCentimos));
  regla();
  partir(`SON: ${m.son}`, COLUMNAS).forEach((t) => add(t));
  if (m.pago?.metodo) add(`FORMA DE PAGO: ${m.pago.metodo.toUpperCase()}`);
  if (m.pago?.voucher) add(`Voucher: ${m.pago.voucher}`);
  add(`COND. VENTA: ${m.pago?.condicion || 'CONTADO'}`);
  regla();
  (m.pie || []).filter(Boolean).forEach((t) => centrado(t));
  return r;
}

// WinAnsi (la codificación de las fuentes estándar del PDF): tildes, ñ, ¡¿, ·, ×, —.
const WIN_ANSI = { '€': 0x80, '…': 0x85, '‘': 0x91, '’': 0x92, '“': 0x93, '”': 0x94, '•': 0x95, '–': 0x96, '—': 0x97 };
export const aWinAnsi = (texto) => Array.from(String(texto), (c) => {
  const codigo = c.codePointAt(0);
  if (WIN_ANSI[c]) return String.fromCharCode(WIN_ANSI[c]);
  return codigo < 0x80 || (codigo >= 0xa0 && codigo <= 0xff) ? c : '?';
}).join('').replace(/[\\()]/g, '\\$&');

const ESTILOS = {
  titulo: { fuente: 'F2', tam: 12, alto: 15, centrar: true },
  subtitulo: { fuente: 'F2', tam: 9.5, alto: 14, centrar: true },
  centro: { fuente: 'F1', tam: CUERPO, alto: 11, centrar: true },
  texto: { fuente: 'F1', tam: CUERPO, alto: 11 },
  negrita: { fuente: 'F2', tam: 10.5, alto: 14 },
  total: { fuente: 'F2', tam: 12, alto: 17 },
  regla: { alto: 9 },
};

// Bytes binarios (el JPEG del logo) como texto latin1, por tramos: sin desbordar la pila.
const binarioATexto = (bytes) => {
  let texto = '';
  for (let i = 0; i < bytes.length; i += 8192) texto += String.fromCharCode.apply(null, bytes.subarray(i, i + 8192));
  return texto;
};

const cargarImagen = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

/**
 * Logo del gym listo para el PDF: sin márgenes, sobre blanco, en JPEG (el PDF lo
 * lee tal cual). Acepta PNG, JPG o WebP. Sin logo o sin canvas, null: el ticket
 * sale solo con el nombre.
 * @returns {Promise<{ bytes: Uint8Array, ancho: number, alto: number } | null>}
 */
export const prepararLogoPdf = async (dataUrl, { lado = 360 } = {}) => {
  if (!/^data:image\/(png|jpeg|webp);base64,/.test(dataUrl || '')) return null;
  try {
    const img = await cargarImagen((await recortarMargenLogo(dataUrl)) || dataUrl);
    if (!img.naturalWidth || !img.naturalHeight) return null;
    const escala = Math.min(1, lado / Math.max(img.naturalWidth, img.naturalHeight));
    const lienzo = document.createElement('canvas');
    lienzo.width = Math.max(1, Math.round(img.naturalWidth * escala));
    lienzo.height = Math.max(1, Math.round(img.naturalHeight * escala));
    const ctx = lienzo.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, lienzo.width, lienzo.height);
    ctx.drawImage(img, 0, 0, lienzo.width, lienzo.height);
    const jpg = lienzo.toDataURL('image/jpeg', 0.9);
    if (!jpg.startsWith('data:image/jpeg;')) return null;
    return { bytes: Uint8Array.from(atob(jpg.split(',')[1]), (c) => c.charCodeAt(0)), ancho: lienzo.width, alto: lienzo.height };
  } catch {
    return null;
  }
};

/**
 * Bytes del PDF (una página del alto del ticket).
 * @param {Object} datos ver renglonesTicket
 * @param {{ logo?: { bytes: Uint8Array, ancho: number, alto: number } | null }} [opciones] logo de prepararLogoPdf
 */
export function crearPdfTicket(datos, { logo = null } = {}) {
  const renglones = renglonesTicket(datos);
  const escalaLogo = logo ? Math.min(LOGO_MAX_ANCHO / logo.ancho, LOGO_MAX_ALTO / logo.alto) : 0;
  const logoAncho = logo ? logo.ancho * escalaLogo : 0;
  const logoAlto = logo ? logo.alto * escalaLogo : 0;
  const altoLogo = logo ? logoAlto + 8 : 0;
  const alto = Math.max(200, renglones.reduce((s, x) => s + ESTILOS[x.estilo].alto, 0) + altoLogo + 2 * MARGEN);
  let y = alto - MARGEN;
  const ops = [];
  if (logo) {
    y -= logoAlto;
    ops.push(`q ${logoAncho.toFixed(2)} 0 0 ${logoAlto.toFixed(2)} ${((ANCHO - logoAncho) / 2).toFixed(2)} ${y.toFixed(2)} cm /Im1 Do Q`);
    y -= 8;
  }
  for (const { texto, estilo, derecha } of renglones) {
    const e = ESTILOS[estilo];
    y -= e.alto;
    if (estilo === 'regla') {
      // Línea fina gris en lugar de guiones.
      const yl = (y + e.alto / 2).toFixed(2);
      ops.push(`q 0.6 w 0.55 G ${MARGEN.toFixed(2)} ${yl} m ${(ANCHO - MARGEN).toFixed(2)} ${yl} l S Q`);
      continue;
    }
    const ancho = (t) => t.length * e.tam * 0.6;
    const x = e.centrar ? Math.max(MARGEN, (ANCHO - ancho(texto)) / 2) : MARGEN;
    if (texto) ops.push(`BT /${e.fuente} ${e.tam} Tf ${x.toFixed(2)} ${y.toFixed(2)} Td (${aWinAnsi(texto)}) Tj ET`);
    if (derecha) ops.push(`BT /${e.fuente} ${e.tam} Tf ${(ANCHO - MARGEN - ancho(derecha)).toFixed(2)} ${y.toFixed(2)} Td (${aWinAnsi(derecha)}) Tj ET`);
  }
  const contenido = ops.join('\n');
  const recursos = `/Font << /F1 4 0 R /F2 5 0 R >>${logo ? ' /XObject << /Im1 7 0 R >>' : ''}`;
  const objetos = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${ANCHO} ${alto.toFixed(2)}] /Resources << ${recursos} >> /Contents 6 0 R >>`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Courier /Encoding /WinAnsiEncoding >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Courier-Bold /Encoding /WinAnsiEncoding >>',
    `<< /Length ${contenido.length} >>\nstream\n${contenido}\nendstream`,
    ...(logo ? [`<< /Type /XObject /Subtype /Image /Width ${logo.ancho} /Height ${logo.alto} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${logo.bytes.length} >>\nstream\n${binarioATexto(logo.bytes)}\nendstream`] : []),
  ];
  let pdf = '%PDF-1.4\n%\xe2\xe3\xcf\xd3\n';
  const posiciones = objetos.map((objeto, i) => {
    const posicion = pdf.length;
    pdf += `${i + 1} 0 obj\n${objeto}\nendobj\n`;
    return posicion;
  });
  const xref = pdf.length;
  pdf += `xref\n0 ${objetos.length + 1}\n0000000000 65535 f \n${posiciones.map((p) => `${String(p).padStart(10, '0')} 00000 n \n`).join('')}`;
  pdf += `trailer\n<< /Size ${objetos.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`;
  // Cada carácter ya es un byte (WinAnsi y el JPEG en latin1): latin1 directo.
  return Uint8Array.from(pdf, (c) => c.charCodeAt(0) & 0xff);
}

/** Nombre de archivo seguro: «ticket-gym-demo-WEB-000013.pdf». */
export const nombreArchivoTicket = (...partes) => `${partes.filter(Boolean).join('-')
  .normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').slice(0, 80) || 'ticket'}.pdf`;

/** ¿El dispositivo puede compartir el archivo (menú del celular con WhatsApp)? */
export const puedeCompartirPdf = () => {
  try {
    return typeof File === 'function' && typeof navigator !== 'undefined' && typeof navigator.canShare === 'function'
      && navigator.canShare({ files: [new File([new Uint8Array(1)], 'x.pdf', { type: 'application/pdf' })] });
  } catch { return false; }
};

/** Comparte el PDF (celular) o lo descarga. Devuelve 'compartido' | 'cancelado' | 'descargado'. */
export async function entregarPdf(bytes, nombre, { compartir = false, texto = '' } = {}) {
  if (compartir && puedeCompartirPdf()) {
    try {
      await navigator.share({ files: [new File([bytes], nombre, { type: 'application/pdf' })], title: nombre, ...(texto ? { text: texto } : {}) });
      return 'compartido';
    } catch (e) {
      if (e?.name === 'AbortError') return 'cancelado';
    }
  }
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const enlace = Object.assign(document.createElement('a'), { href: url, download: nombre });
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
  return 'descargado';
}
